import { useEffect, useState, type ReactNode } from "react"
import { NavLink, useLocation } from "react-router"
import { ChevronRight } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip"
import { useDocsHref } from "~/docs-data/docs-link"
import { cn } from "~/lib/utils"

// Short delay so the tooltip only appears when a reader pauses on a row,
// not on every flick of the mouse across the nav. Overrides the sidebar's
// provider-level delayDuration={0} via this per-tooltip prop.
const TOOLTIP_DELAY_MS = 500

function LabelWithTooltip({ label }: { label: string }) {
  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>
        <span className="flex-1 truncate">{label}</span>
      </TooltipTrigger>
      <TooltipContent side="right" align="center">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

export interface DocsSidebarSection {
  label?: string
  /** Optional leading icon rendered inline next to the section label. */
  icon?: ReactNode
  /**
   * Optional destination — when set, the section header becomes clickable
   * (e.g. linking a "Place" / "Area" section header to the location-type
   * concept page). Visual style stays unchanged.
   */
  to?: string
  items: DocsSidebarItem[]
}

export interface DocsSidebarItem {
  /**
   * Destination path. Omit when the item is purely a grouping header — the
   * row renders as a non-link label that still toggles its children open or
   * closed when clicked. Items without `to` MUST have either `children` or
   * `kind: "subheader"` (otherwise they have no purpose).
   */
  to?: string
  label: string
  /** When true, link should match exactly (use react-router `end`). */
  end?: boolean
  /**
   * Extra absolute docs paths that also mark this item active
   * (prefix-matched). Used when one nav entry represents several pages,
   * e.g. a canonical metric-concept link whose concrete variant pages live
   * under different ids.
   */
  activePaths?: string[]
  children?: DocsSidebarItem[]
  /** Optional leading icon (rendered inline before the label). */
  icon?: ReactNode
  /** When true, the children list starts expanded even if no descendant is active. */
  defaultOpen?: boolean
  /**
   * When `"subheader"`, render the row as a non-interactive visual divider
   * grouping the items beneath it (e.g. "Metrics" / "Maps and datasets"
   * inside a Place section). Sub-headers ignore `to`, `children`,
   * `defaultOpen`, and the active-state styling.
   */
  kind?: "subheader"
}

// Indent scales linearly with depth so children/grandchildren visually nest.
// Inline style avoids Tailwind purge problems with dynamic class names.
const indentPxForDepth = (depth: number) => 8 + depth * 16

function SidebarLink({
  item,
  depth = 0,
  trailing,
  onLabelClick,
}: {
  item: DocsSidebarItem
  depth?: number
  trailing?: ReactNode
  /**
   * Click handler for the label part of a non-link row. When `item.to` is
   * undefined the entire row acts as a button that triggers this — used by
   * grouping headers to toggle their children open/closed.
   */
  onLabelClick?: () => void
}) {
  const dhref = useDocsHref()
  const { pathname } = useLocation()
  const sharedClass =
    "flex items-center gap-2 py-1.5 pr-2 rounded-md transition-colors w-full text-left"
  const padding = { paddingLeft: `${indentPxForDepth(depth)}px` }
  // Pages this item represents beyond its own `to` (see activePaths).
  const extraActive = (item.activePaths ?? []).some((p) => {
    const target = dhref(p)
    return pathname === target || pathname.startsWith(`${target}/`)
  })

  if (item.to) {
    return (
      <NavLink
        to={dhref(item.to)}
        end={item.end}
        style={padding}
        className={({ isActive }) =>
          cn(
            sharedClass,
            // Active must be unmistakable next to the accent-tint hover
            // state, so it uses the solid brand primary (deep forest on
            // white text) rather than a stronger tint.
            isActive || extraActive
              ? "bg-primary text-primary-foreground font-medium"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
          )
        }
      >
        {item.icon && <span className="shrink-0">{item.icon}</span>}
        <LabelWithTooltip label={item.label} />
        {trailing}
      </NavLink>
    )
  }

  // Non-link grouping header — same look as a leaf row, but clicking
  // anywhere on the row toggles the group via `onLabelClick`. Trailing
  // chevron button is still rendered so screen-reader users can target it
  // directly.
  return (
    <button
      type="button"
      onClick={onLabelClick}
      style={padding}
      className={cn(
        sharedClass,
        "text-muted-foreground hover:text-foreground hover:bg-accent/50",
      )}
    >
      {item.icon && <span className="shrink-0">{item.icon}</span>}
      <LabelWithTooltip label={item.label} />
      {trailing}
    </button>
  )
}

/**
 * Recursively check whether `pathname` falls inside the subtree rooted at
 * `item` — used to auto-open ancestor groups when navigating into a leaf.
 */
function isPathnameInBranch(
  pathname: string,
  item: DocsSidebarItem,
  prefix: (to: string) => string,
): boolean {
  for (const raw of [item.to, ...(item.activePaths ?? [])]) {
    if (!raw) continue
    const to = prefix(raw)
    if (pathname === to || pathname.startsWith(`${to}/`)) return true
  }
  return (item.children ?? []).some((c) =>
    isPathnameInBranch(pathname, c, prefix),
  )
}

/**
 * Renders a parent item whose children list collapses behind a chevron.
 * The group auto-opens when the current route matches anywhere in its
 * subtree (so navigating into a grandchild reveals all ancestors). Children
 * that themselves have children render as nested CollapsibleItems —
 * supports arbitrary depth.
 */
function CollapsibleItem({
  item,
  depth = 0,
}: {
  item: DocsSidebarItem
  depth?: number
}) {
  const { pathname } = useLocation()
  const dhref = useDocsHref()
  const isInBranch = isPathnameInBranch(pathname, item, dhref)
  const [open, setOpen] = useState(isInBranch || !!item.defaultOpen)

  // Re-open the group whenever the current page lives inside it — covers
  // deep links into a collapsed group, and navigating between pages of a
  // group the user had manually collapsed. Keyed on pathname (not just
  // isInBranch) so it re-asserts on every navigation, ensuring the active
  // page is never hidden behind a closed chevron.
  useEffect(() => {
    if (isInBranch) setOpen(true)
  }, [pathname, isInBranch])

  const toggle = () => setOpen((v) => !v)

  return (
    <li>
      <SidebarLink
        item={item}
        depth={depth}
        onLabelClick={toggle}
        trailing={
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              toggle()
            }}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
            aria-label={open ? `Collapse ${item.label}` : `Expand ${item.label}`}
          >
            <ChevronRight
              className={cn("h-3 w-3 transition-transform", open && "rotate-90")}
            />
          </button>
        }
      />
      {open && item.children && item.children.length > 0 && (
        <ul className="space-y-0.5 mt-0.5">
          {item.children.map((child) =>
            child.children && child.children.length > 0 ? (
              <CollapsibleItem
                key={child.to ?? child.label}
                item={child}
                depth={depth + 1}
              />
            ) : (
              <li key={child.to ?? child.label}>
                <SidebarLink item={child} depth={depth + 1} />
              </li>
            ),
          )}
        </ul>
      )}
    </li>
  )
}

export function DocsSidebarNav({
  sections,
}: {
  sections: DocsSidebarSection[]
}) {
  const dhref = useDocsHref()
  return (
    <nav className="p-4 text-sm">
      {sections.map((section, i) => {
        const hasLabel = !!section.label
        // First labelled section gets no top divider (the topbar already
        // separates the nav from the country switcher). Subsequent
        // labelled sections get a hairline rule so the reader sees where
        // one group ends and the next begins.
        const dividerClass =
          i > 0 && hasLabel ? "mt-5 pt-4 border-t border-border/60" : "mt-5"
        return (
          <div key={i} className={i === 0 ? "" : dividerClass}>
            {section.label && (
              section.to ? (
                // Clickable section header — styled like a nav item with
                // active/hover state, but slightly bolder so it still reads
                // as a category for the items nested below it.
                <NavLink
                  to={dhref(section.to)}
                  end
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 py-1.5 px-2 mb-1 rounded-md transition-colors text-sm font-semibold",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-foreground hover:bg-accent/50",
                    )
                  }
                >
                  {section.icon && <span className="shrink-0">{section.icon}</span>}
                  <span>{section.label}</span>
                </NavLink>
              ) : (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground uppercase tracking-wide mb-2 px-2">
                  {section.icon && <span className="shrink-0">{section.icon}</span>}
                  <span>{section.label}</span>
                </div>
              )
            )}
            {/* Items inside a labelled section indent themselves visually
                under the title by sitting in a left-bordered container.
                The border + extra left padding make it unambiguous that
                a row belongs to the group above it. */}
            <ul
              className={
                hasLabel
                  ? "space-y-0.5 border-l border-border/60 ml-3 pl-1"
                  : "space-y-0.5"
              }
            >
              {section.items.map((item) => {
                if (item.kind === "subheader") {
                  return (
                    <li
                      key={`subheader-${item.label}`}
                      className="px-2 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80"
                    >
                      {item.label}
                    </li>
                  )
                }
                return item.children && item.children.length > 0 ? (
                  <CollapsibleItem key={item.to ?? item.label} item={item} />
                ) : (
                  <li key={item.to ?? item.label}>
                    <SidebarLink item={item} />
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </nav>
  )
}
